import { useRef, useState, useEffect } from "react";

const Player = ({ source, frequencyData }) => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [volume, setVolume] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    const togglePlayPause = () => {
        if (isPlaying) {
            if (audioRef.current) {
                audioRef.current.pause();
            }
        } else {
            if (audioRef.current) {
                audioRef.current.play();
            }
        }
        setIsPlaying(!isPlaying);
    };

    const handleTimeUpdate = () => {
        setCurrentTime(audioRef.current.currentTime);
        setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
    };

    const handleLoadedMetadata = () => {
        setDuration(audioRef.current.duration);
    };

    const handleVolumeChange = (e) => {
        const volume = e.target.value / 100;
        setVolume(volume * 100);
        if (audioRef.current) {
            audioRef.current.volume = volume;
        }
    };

    const handleSeek = (e) => {
        const seekTo = (e.target.value / 100) * audioRef.current.duration;
        if (audioRef.current) {
            audioRef.current.currentTime = seekTo;
        }
    };

    useEffect(() => {
        const audio = audioRef.current;

        if (audio) {
            audio.addEventListener("timeupdate", handleTimeUpdate);
            audio.addEventListener("loadedmetadata", handleLoadedMetadata);
        }

        return () => {
            if (audio) {
                audio.removeEventListener("timeupdate", handleTimeUpdate);
                audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
            }
        };
    }, [source]);

    useEffect(() => {
        if (audioRef.current && frequencyData?.frequency_volume != null) {
            const volume = frequencyData.frequency_volume / 100;
            audioRef.current.volume = volume;
            setVolume(volume * 100);
        }
    }, [frequencyData]);

    const formatTime = (timeInSeconds) => {
        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = Math.floor(timeInSeconds % 60);
        return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
    };

    return (
        <div className="card rounded-xl px-3">
            <div className="card-body">
                {/* User Info Section */}
                <div className="h4 text-center p-2">
                    Frequency Player
                </div>

                {/* Hidden Audio Element */}
                {source && <audio ref={audioRef} src={source} style={{ display: "none" }} />}

                {/* Play/Pause Button */}
                <div className="d-flex justify-content-center mb-4">
                    {source ? < button
                        onClick={togglePlayPause}
                        className="btn btn-primary btn-lg rounded-circle shadow-lg"
                        style={{
                            width: '64px',
                            height: '64px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(145deg, #3a8ffe, #0063e6)',
                            border: 'none',
                            transition: 'transform 0.2s',
                        }}
                        onMouseOver={e => e.target.style.transform = 'scale(1.05)'}
                        onMouseOut={e => e.target.style.transform = 'scale(1)'}
                    >
                        <i className={`ri-${isPlaying ? 'pause' : 'play'}-fill fs-3`}></i>
                    </button> : "loading..."}
                </div>

                {/* Progress Section */}
                <div className="mb-4">
                    <input
                        type="range"
                        className="form-range"
                        min="0"
                        max="100"
                        value={progress}
                        onChange={handleSeek}
                        style={{
                            height: '6px',
                            cursor: 'pointer',
                        }}
                    />
                    <div className="d-flex justify-content-between mt-2 text-muted">
                        <small>{formatTime(currentTime)}</small>
                        <small>{formatTime(duration)}</small>
                    </div>
                </div>

                {/* Volume Controls */}
                <div className="row g-4">
                    <div>
                        <div className="volume-control p-3 bg-black bg-opacity-25 rounded-3">
                            <div className="d-flex align-items-center mb-2">
                                <i className="ri-music-2-line me-2"></i>
                                <span className="text-dark">Frequency Volume {Math.floor(volume)}</span>
                            </div>
                            <input
                                type="range"
                                className="form-range"
                                min="0"
                                max="100"
                                step="0.1"
                                value={volume}
                                onChange={handleVolumeChange}
                                style={{ height: '4px' }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Player;
